/**
 * grantGuard — lifetime access grants outrank every billing provider.
 *
 * WHY THIS FILE EXISTS
 * --------------------
 * Alpha/lifetime testers are granted access with an invite code. The grant
 * is server-verified and stored at `users/{uid}/alpha/grant`; the client
 * marks it on the subscription as `alphaTester: true`. These people NEVER
 * PURCHASED ANYTHING, so a real store provider (RevenueCat) will correctly
 * and permanently answer "no active entitlement" for every one of them.
 *
 * If a provider answer is ever written straight onto `subscription`, the
 * grant is erased locally, the sync funnel pushes that erasure to the
 * cloud, and it propagates to every device the person owns. The people who
 * tested the app for free would silently lose the lifetime access they were
 * promised — on the day billing goes live, which is the worst possible day
 * for a trust failure.
 *
 * THE INVARIANT (SEAM_REGISTRY.md seam 6, REVENUECAT_ROLLOUT.md):
 *
 *     premium = (store entitlement) OR (lifetime grant)
 *
 * A provider may GRANT access. It may never REVOKE a grant.
 *
 * This applies to grants issued in the future exactly as it does to the
 * original 19 — codes are still being issued from the reserve, and some
 * will be redeemed after billing is live (founder rule, 24 Aug 2026).
 *
 * RULE FOR THE RevenueCat IMPLEMENTATION: every value returned by a
 * BillingProvider passes through `preserveGrant()` before it reaches the
 * store. There is no exception, including `restore()` — which today has no
 * call site and will get one.
 */

import type { Subscription } from '../types';

/**
 * The single definition of "this account holds a lifetime grant".
 * Read the FLAG, never the plan string: the plan is what a provider
 * overwrites, the flag is what must survive it.
 */
export function hasLifetimeGrant(sub: Subscription | undefined | null): boolean {
  return sub?.alphaTester === true;
}

/**
 * The canonical shape of a granted subscription. One definition, so a
 * grant applied in 2027 is identical to one applied in 2026.
 * `trialStartedAt` is carried through: it records that a trial was once
 * used and must not be resurrected by the grant.
 */
export function grantSubscription(current?: Subscription): Subscription {
  return {
    plan: 'lifetime',
    trialStartedAt: current?.trialStartedAt,
    alphaTester: true,
  };
}

/**
 * Merge a billing provider's answer onto the current subscription without
 * ever dropping a lifetime grant.
 *
 * The grant is honoured if EITHER side carries it: `current` covers the
 * local holder whose provider says "nothing"; `incoming` covers a grant
 * arriving from the cloud (restoreAlphaGrant / remote snapshot) on a fresh
 * device that has not seen it yet.
 *
 * Returns `incoming` unchanged when no grant is involved, so ordinary
 * paying users are completely unaffected by this guard.
 */
export function preserveGrant(current: Subscription, incoming: Subscription): Subscription {
  if (!hasLifetimeGrant(current) && !hasLifetimeGrant(incoming)) return incoming;
  return {
    plan: 'lifetime',
    // Keep whichever trial history exists — a grant must not hand back a
    // fresh 7-day trial to someone who already spent theirs.
    trialStartedAt: current.trialStartedAt ?? incoming.trialStartedAt,
    alphaTester: true,
  };
}
